import React, { useMemo, useState, useEffect } from 'react'
import { ChevronDown, Music, Plus, X } from 'lucide-react'
import { MultiTrackMixPlayer } from './MultiTrackMixPlayer'
import { getVisualizerAssetUrl } from '@/config/visualizerAssets'
import { getTags, addTag, removeTag } from '@/services/tagService'
import { getTagColor } from '@/types/tags'

interface TrackInfo {
  name: string
  artist: string
  albumCover?: string
  bpm?: number
  genre?: string
  year?: string
}

interface MixPairData {
  index: number
  track_prev: {
    hashName: string
    hash: string
    song: string
    artist: string
    artistName?: string
    trackName?: string
    bpm: number
  }
  track_next: {
    hashName: string
    hash: string
    song: string
    artist: string
    artistName?: string
    trackName?: string
    bpm: number
  }
  presyncout_length_seconds?: number
  syncout_length_seconds?: number
  merge_info?: {
    presyncout_length_seconds: number
    presyncout_length_samples: number
    syncout_length_seconds: number
    syncout_length_samples: number
  }
}

interface FloatingMusicPlayerProps {
  prevTrack: TrackInfo
  nextTrack: TrackInfo
  mixPairData?: MixPairData
  sequenceId?: string
  isOpen: boolean
  onClose: () => void
}

// 根据年份获取颜色类
const getYearColorClasses = (year?: string): string => {
  if (!year) return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  
  const yearNum = parseInt(year)
  
  if (yearNum < 1970) {
    // 1960s及以前 - 深棕复古色
    return 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
  } else if (yearNum < 1980) {
    // 1970s - 橙色怀旧
    return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
  } else if (yearNum < 1990) {
    // 1980s - 霓虹粉紫
    return 'bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-800 dark:text-fuchsia-300'
  } else if (yearNum < 2000) {
    // 1990s - 蓝绿色
    return 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300'
  } else if (yearNum < 2010) {
    // 2000s - 蓝色
    return 'bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300'
  } else if (yearNum < 2020) {
    // 2010s - 现代灰蓝
    return 'bg-slate-100 dark:bg-slate-700/60 text-slate-800 dark:text-slate-300'
  } else {
    // 2020s+ - 未来感青色
    return 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300'
  }
}

// 根据流派获取颜色类
const getGenreColorClasses = (genre?: string): string => {
  if (!genre) return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  
  const genreLower = genre.toLowerCase()
  
  if (genreLower.includes('rock') || genreLower.includes('metal')) {
    return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
  } else if (genreLower.includes('pop')) {
    return 'bg-pink-100 dark:bg-pink-900/40 text-pink-800 dark:text-pink-300'
  } else if (genreLower.includes('hip') || genreLower.includes('rap')) {
    return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
  } else if (genreLower.includes('electronic') || genreLower.includes('edm') || genreLower.includes('techno') || genreLower.includes('house')) {
    return 'bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300'
  } else if (genreLower.includes('jazz')) {
    return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
  } else if (genreLower.includes('classical')) {
    return 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300'
  } else if (genreLower.includes('country') || genreLower.includes('folk')) {
    return 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
  } else if (genreLower.includes('r&b') || genreLower.includes('soul')) {
    return 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300'
  } else if (genreLower.includes('indie') || genreLower.includes('alternative')) {
    return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'
  } else if (genreLower.includes('reggae')) {
    return 'bg-lime-100 dark:bg-lime-900/40 text-lime-800 dark:text-lime-300'
  } else if (genreLower.includes('disco') || genreLower.includes('funk')) {
    return 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300'
  } else {
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  }
}

export const FloatingMusicPlayer: React.FC<FloatingMusicPlayerProps> = ({
  prevTrack,
  nextTrack,
  mixPairData,
  sequenceId,
  isOpen,
  onClose,
}) => {
  // Tag 管理状态
  const [tags, setTags] = useState<string[]>([])
  const [allTags, setAllTags] = useState<string[]>([])
  const [isAddingTag, setIsAddingTag] = useState(false)
  const [newTagInput, setNewTagInput] = useState('')
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([])

  // 加载tags
  useEffect(() => {
    const loadTags = async () => {
      if (mixPairData?.index !== undefined) {
        const tagData = await getTags()
        setTags(tagData.mixPairTags[mixPairData.index] || [])
        setAllTags(tagData.allTags || [])
      }
    }
    loadTags()
  }, [mixPairData?.index])

  // 更新建议列表
  useEffect(() => {
    if (newTagInput.trim()) {
      const input = newTagInput.toLowerCase()
      const suggestions = allTags.filter(tag => 
        tag.toLowerCase().includes(input) && !tags.includes(tag)
      )
      setFilteredSuggestions(suggestions.slice(0, 5))
    } else {
      setFilteredSuggestions([])
    }
  }, [newTagInput, allTags, tags])

  // 添加tag
  const handleAddTag = async (tagName: string) => {
    if (mixPairData?.index === undefined) return
    if (!tagName.trim()) return
    if (tags.includes(tagName)) return

    try {
      const updatedTagData = await addTag(mixPairData.index, tagName.trim())
      setTags(updatedTagData.mixPairTags[mixPairData.index] || [])
      setAllTags(updatedTagData.allTags || [])
      setNewTagInput('')
      setIsAddingTag(false)
    } catch (error) {
      console.error('Failed to add tag:', error)
    }
  }

  // 删除tag
  const handleRemoveTag = async (tagName: string) => {
    if (mixPairData?.index === undefined) return

    try {
      const updatedTagData = await removeTag(mixPairData.index, tagName)
      setTags(updatedTagData.mixPairTags[mixPairData.index] || [])
      setAllTags(updatedTagData.allTags || [])
    } catch (error) {
      console.error('Failed to remove tag:', error)
    }
  }

  // 构建 queueDetails 对象供 MultiTrackMixPlayer 使用
  const queueDetails = useMemo(() => {
    if (!mixPairData || !sequenceId) return null
    
    // 转换 mixPairData 为 MultiTrackMixPlayer 期望的格式
    const mixPair = {
      pair_index: mixPairData.index,  // 使用 mixPairData.index 作为 pair_index
      mix_situation_name: 'Unknown',
      is_success: true,
      error_code: 0,
      error_message: '',
      debug_status: 0,
      mixtability: {
        bpm: {
          difference_percent: 0,
          threshold: 30,
          pass: true
        },
        genre: {
          compatibility_score: 1,
          compatibility_reason: 'Compatible',
          pass: true
        },
        score_factors: [],
        overall: {
          pass: true,
          reason: 'Compatible',
          score: 1
        }
      },
      bpm_difference_percent: 0,
      transition_nbeats: 16,
      automation_preset: 'default',
      track_prev: {
        artist: mixPairData.track_prev.artistName || mixPairData.track_prev.artist,
        song: mixPairData.track_prev.trackName || mixPairData.track_prev.song,
        hash: mixPairData.track_prev.hashName || mixPairData.track_prev.hash,
        bpm: mixPairData.track_prev.bpm
      },
      track_next: {
        artist: mixPairData.track_next.artistName || mixPairData.track_next.artist,
        song: mixPairData.track_next.trackName || mixPairData.track_next.song,
        hash: mixPairData.track_next.hashName || mixPairData.track_next.hash,
        bpm: mixPairData.track_next.bpm
      },
      merge_info: mixPairData.merge_info || {
        presyncout_length_seconds: mixPairData.presyncout_length_seconds || 0,
        presyncout_length_samples: 0,
        syncout_length_seconds: mixPairData.syncout_length_seconds || 0,
        syncout_length_samples: 0
      }
    }
    
    return {
      id: sequenceId,
      name: 'Visualization Mix',
      category: 'visualization',
      instance: 'visualization',
      tracks: [],
      transitions: [],
      transitionsMerged: [],
      mixinfo: {
        mix_pairs: [mixPair],
        session_id: sequenceId,
        sequencing_method: 'visualization'
      },
      trackCount: 0,
      transitionCount: 0,
      transitionsMergedCount: 0
    }
  }, [mixPairData, sequenceId])
  
  if (!isOpen) return null
  
  return (
    <>
      {/* 遮罩层 */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* 悬浮播放器 */}
      <div className="fixed inset-x-0 bottom-0 z-50 transition-transform duration-300 ease-out px-4 sm:px-0">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl border-2 border-gray-200 dark:border-gray-600 overflow-hidden">
          {/* 顶部拖动条 */}
          <div className="flex justify-center py-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors"
            />
          </div>
          
          {/* 标题和Tags */}
          <div className="px-6 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Mix Transition
                </h3>
                {mixPairData && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    #{mixPairData.index}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
                title="关闭"
              >
                <ChevronDown size={20} />
              </button>
            </div>

            {/* Tags区域 */}
            <div className="flex items-center gap-2 flex-wrap">
              {tags.map((tag) => {
                const color = getTagColor(tag)
                return (
                  <span
                    key={tag}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${color.bg} ${color.text} border ${color.border}`}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:opacity-70 transition-opacity"
                      title="删除标签"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )
              })}
              
              {/* 添加tag按钮 */}
              {!isAddingTag ? (
                <button
                  onClick={() => setIsAddingTag(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600"
                  title="添加标签"
                >
                  <Plus size={12} />
                  添加标签
                </button>
              ) : (
                <div className="relative inline-block">
                  <input
                    type="text"
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTag(newTagInput)
                      } else if (e.key === 'Escape') {
                        setIsAddingTag(false)
                        setNewTagInput('')
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setIsAddingTag(false)
                        setNewTagInput('')
                      }, 200)
                    }}
                    placeholder="输入标签..."
                    autoFocus
                    className="px-2 py-1 text-xs border border-primary-300 dark:border-primary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    style={{ width: '120px' }}
                  />
                  {/* 建议列表 */}
                  {filteredSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                      {filteredSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => handleAddTag(suggestion)}
                          className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* 两首歌曲信息并排展示 */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 gap-6 relative">
              {/* 前一首歌 */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Prev Track
                  </span>
                </div>
                
                {/* 专辑封面 */}
                <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-blue-500 to-blue-700">
                  {prevTrack.albumCover ? (
                    <img 
                      src={prevTrack.albumCover} 
                      alt={prevTrack.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white text-6xl font-bold">
                      ♪
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                
                {/* 歌曲信息 */}
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-gray-900 dark:text-white line-clamp-2">
                    {prevTrack.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                    {prevTrack.artist}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {prevTrack.bpm && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {Math.round(prevTrack.bpm)} BPM
                      </span>
                    )}
                    {prevTrack.year && (
                      <span className={`px-2 py-0.5 rounded ${getYearColorClasses(prevTrack.year)}`}>
                        {prevTrack.year}
                      </span>
                    )}
                    {prevTrack.genre && (
                      <span className={`px-2 py-0.5 rounded ${getGenreColorClasses(prevTrack.genre)}`}>
                        {prevTrack.genre}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* 转场图标 - 黑胶碟片叠加 */}
              <div className="absolute left-1/2 top-[45%] transform -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="relative w-24 h-16">
                  {/* 背景光晕 */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-full blur-xl"></div>
                  
                  {/* 后面的碟片 (蓝色) */}
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg border-2 border-white dark:border-gray-700 transform -rotate-12">
                    {/* 中心孔 */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-gray-900 dark:bg-gray-200"></div>
                    </div>
                    {/* 纹理线条 */}
                    <div className="absolute inset-2 rounded-full border border-blue-300/30"></div>
                    <div className="absolute inset-3 rounded-full border border-blue-300/20"></div>
                    <div className="absolute inset-4 rounded-full border border-blue-300/10"></div>
                  </div>
                  
                  {/* 前面的碟片 (紫色) */}
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg border-2 border-white dark:border-gray-700 transform rotate-12">
                    {/* 中心孔 */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-gray-900 dark:bg-gray-200"></div>
                    </div>
                    {/* 纹理线条 */}
                    <div className="absolute inset-2 rounded-full border border-purple-300/30"></div>
                    <div className="absolute inset-3 rounded-full border border-purple-300/20"></div>
                    <div className="absolute inset-4 rounded-full border border-purple-300/10"></div>
                  </div>
                </div>
              </div>
              
              {/* 后一首歌 */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Next Track
                  </span>
                </div>
                
                {/* 专辑封面 */}
                <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-purple-500 to-purple-700">
                  {nextTrack.albumCover ? (
                    <img 
                      src={nextTrack.albumCover} 
                      alt={nextTrack.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white text-6xl font-bold">
                      ♫
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                
                {/* 歌曲信息 */}
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-gray-900 dark:text-white line-clamp-2">
                    {nextTrack.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                    {nextTrack.artist}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {nextTrack.bpm && (
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                        {Math.round(nextTrack.bpm)} BPM
                      </span>
                    )}
                    {nextTrack.year && (
                      <span className={`px-2 py-0.5 rounded ${getYearColorClasses(nextTrack.year)}`}>
                        {nextTrack.year}
                      </span>
                    )}
                    {nextTrack.genre && (
                      <span className={`px-2 py-0.5 rounded ${getGenreColorClasses(nextTrack.genre)}`}>
                        {nextTrack.genre}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* 播放器区域 - 集成 MultiTrackMixPlayer */}
          <div className="px-6 pb-6">
            {queueDetails ? (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <MultiTrackMixPlayer 
                  queueDetails={queueDetails}
                  currentPairIndex={0}
                  onPairChange={() => {}}
                  audioPathPrefix={getVisualizerAssetUrl('mixes/transitions_merged')}
                />
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <Music className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-2" />
                  <p className="text-sm">💡 等待加载音频数据...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
